"use client";
import { useState } from "react";
import { useCart } from "./CartContext";
import { Button } from "./ui/Button";
import { Badge } from "./ui/Badge";
import { X, Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { CheckoutModal } from "./CheckoutModal";

export function CartDrawer() {
  const { items, isOpen, closeCart, removeItem, updateQuantity, totalItems, clearCart } = useCart();
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);

  if (!isOpen) return null;

  const calculateTotal = () => {
    return items.reduce((sum, item) => {
      const price = parseFloat(item.totalPrice.replace(/[R$\s.]/g, '').replace(',', '.'));
      return sum + (price * item.quantity);
    }, 0);
  };

  const formatCurrency = (value: number) => {
    return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100] transition-opacity duration-300"
        onClick={closeCart}
      />

      {/* Drawer */}
      <div className="fixed right-0 inset-y-0 w-full max-w-md bg-white shadow-2xl z-[101] flex flex-col transform transition-transform duration-300 ease-out">
        {/* Header */}
        <div className="border-b border-gray-100 p-6 bg-gray-50/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-[#D4C070] p-2 rounded-lg">
                <ShoppingBag className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Meu Carrinho</h2>
                <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">
                  {totalItems} {totalItems === 1 ? 'item' : 'itens'} selecionados
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={closeCart}
              className="rounded-full hover:bg-gray-200 text-gray-400"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0 p-6 space-y-6 custom-scrollbar">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center space-y-4">
              <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center">
                <ShoppingBag className="h-10 w-10 text-gray-300" />
              </div>
              <div>
                <p className="text-gray-900 font-semibold">Seu carrinho está vazio</p>
                <p className="text-sm text-gray-400 mt-1">Explore nossos produtos e adicione itens aqui.</p>
              </div>
              <Button 
                onClick={closeCart}
                variant="outline"
                className="mt-4 border-[#D4C070] text-[#D4C070] hover:bg-[#D4C070] hover:text-white"
              >
                Continuar Navegando
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => (
                <div
                  key={`${item.id}-${item.paymentType}`}
                  className="group relative bg-white border border-gray-100 rounded-xl p-4 hover:shadow-md transition-all duration-200"
                >
                  <div className="flex gap-4">
                    {/* Image */}
                    <div className="w-24 h-24 bg-gray-50 rounded-lg flex-shrink-0 overflow-hidden border border-gray-50">
                      <ImageWithFallback
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0 flex flex-col justify-between">
                      <div>
                        <div className="flex items-start justify-between">
                          <div className="flex-1 pr-2">
                            <h3 className="font-bold text-gray-900 text-sm leading-tight line-clamp-2">
                              {item.name}
                            </h3>
                            <p className="text-[10px] text-gray-400 uppercase tracking-widest mt-1 font-bold">
                              {item.brand}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                            onClick={() => removeItem(item.id, item.paymentType)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <div className="mt-2">
                          <Badge
                            variant="outline"
                            className={`text-[10px] px-2 py-0 h-5 font-bold uppercase tracking-tighter ${item.paymentType === 'avista'
                                ? 'bg-green-50 text-green-700 border-green-100'
                                : 'bg-[#D4C070]/10 text-[#D4C070] border-[#D4C070]/20'
                              }`}
                          >
                            {item.paymentType === 'avista' ? 'Pagamento à Vista' : 'Pagamento Parcelado'}
                          </Badge>
                        </div>
                      </div>

                      <div className="flex items-center justify-between mt-3">
                        {/* Quantity Controls */}
                        <div className="flex items-center bg-gray-50 rounded-lg p-1 border border-gray-100">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-gray-500 hover:text-gray-900"
                            onClick={() => updateQuantity(item.id, item.paymentType, item.quantity - 1)}
                            disabled={item.reserved}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="text-sm font-bold w-8 text-center text-gray-900">
                            {item.quantity}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-gray-500 hover:text-gray-900"
                            onClick={() => updateQuantity(item.id, item.paymentType, item.quantity + 1)}
                            disabled={item.reserved}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>

                        {/* Price */}
                        <div className="text-right">
                          <p className="font-bold text-gray-900">
                            {item.totalPrice}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-gray-100 p-6 space-y-4 bg-gray-50/50">
            <div className="space-y-2">
              <div className="flex items-center justify-between py-2 border-b border-gray-100/50">
                <span className="text-gray-900 font-bold text-lg">Total</span>
                <span className="text-2xl font-black text-[#849D70]">
                  {formatCurrency(calculateTotal())}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 pt-2">
              <Button
                className="w-full bg-[#D4C070] hover:bg-[#BFAE5E] text-white h-12 font-bold text-lg shadow-lg shadow-[#D4C070]/20 rounded-xl"
                onClick={() => setIsCheckoutOpen(true)}
              >
                FINALIZAR VENDA
              </Button>
              <Button
                variant="ghost"
                className="w-full text-gray-400 hover:text-red-500 hover:bg-red-50 font-medium transition-all"
                onClick={() => {
                  if (confirm('Tem certeza que deseja limpar o carrinho?')) {
                    clearCart();
                  }
                }}
              >
                Esvaziar Carrinho
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Checkout Modal */}
      <CheckoutModal
        isOpen={isCheckoutOpen}
        onClose={() => setIsCheckoutOpen(false)}
        total={calculateTotal()}
      />
    </>
  );
}
